import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { History, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Ocultar botón de historial en la barra de controles (funcionalidad conservada, solo no visible). */
const HISTORY_BUTTON_VISIBLE = false;

/**
 * ControlsBar (AIRecording)
 * Barra de controles visible debajo del navbar del host (cuando está embebido) y con acciones a la derecha.
 * Incluye el cuadro de búsqueda Global Search siguiendo las especificaciones de Booking (bitácora 140126).
 * - Global Search oculto por default, se muestra vía ícono de lupa
 * - Se oculta automáticamente cuando está vacío al perder el foco
 * - Se desplaza hacia la derecha cuando hay un left sidebar presente
 */
export interface ControlsBarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export const ControlsBar: React.FC<ControlsBarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const [globalSearchVisible, setGlobalSearchVisible] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const currentValue = searchValue !== undefined ? searchValue : localSearchValue;

  // Comando de voz ACV: "Abrir barra de controles" → mostrar búsqueda y enfocar
  useEffect(() => {
    const handler = () => {
      setGlobalSearchVisible(true);
      requestAnimationFrame(() => searchInputRef.current?.focus());
    };
    window.addEventListener('acv-show-controls-bar', handler);
    return () => window.removeEventListener('acv-show-controls-bar', handler);
  }, []);

  // Debounce simple para no filtrar en cada keystroke (como en Booking)
  const debouncedSet = useMemo(() => {
    let timer: NodeJS.Timeout | null = null;
    return (value: string) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (onSearchChange) {
          onSearchChange(value || '');
        } else {
          setLocalSearchValue(value || '');
        }
      }, 350);
    };
  }, [onSearchChange]);

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSet(value);
    },
    [debouncedSet]
  );

  const onToggleHistory = () => {
    try {
      window.dispatchEvent(new CustomEvent('acv-toggle-right-sidebar'));
    } catch {
      // ignore
    }
  };

  return (
    <div
      data-acv-controls-bar="true"
      style={{
        flexShrink: 0,
        zIndex: 60,
        height: 65,
        minHeight: 65,
        width: '100%',
        boxSizing: 'border-box',
        background: 'var(--card-bg, var(--bg-primary, #ffffff))',
        borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
        borderRight: '1px solid rgba(0, 0, 0, 0.10)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 12px',
          gap: '10px',
        }}
      >
        {/* Ícono de lupa para mostrar/ocultar Global Search */}
        <button
          type="button"
          onClick={() => setGlobalSearchVisible((v) => !v)}
          title={t('common.search', 'Search')}
          aria-label={t('common.search', 'Search')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-primary, #1a1a1a)',
            fontSize: 18,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--sidebar-item-hover-bg, rgba(0, 0, 0, 0.06))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Search size={18} />
        </button>

        {/* Global Search - visible cuando se hace clic en la lupa */}
        {globalSearchVisible && (
          <div
            style={{
              width: 240,
              maxWidth: '38vw',
            }}
          >
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              defaultValue={currentValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'var(--input-border, 1px solid var(--border-color, #e5e7eb))',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                background: 'var(--input-bg, var(--card-bg, #ffffff))',
                color: 'var(--input-foreground, var(--text-primary, #111827))',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--theme-primary, #1976d2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color, #e5e7eb)';
                // Ocultar si está vacío al perder el foco (como en Booking)
                if (!String(e.target.value || '').trim()) {
                  setGlobalSearchVisible(false);
                }
              }}
            />
          </div>
        )}

        {/* Botón de historial a la derecha - oculto (HISTORY_BUTTON_VISIBLE), funcionalidad conservada */}
        {HISTORY_BUTTON_VISIBLE && (
        <button
          type="button"
          onClick={onToggleHistory}
          title={t('history', 'History')}
          aria-label={t('history', 'History')}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-primary, #1a1a1a)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              'var(--sidebar-item-hover-bg, rgba(0, 0, 0, 0.06))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <History size={18} />
        </button>
        )}
      </div>
    </div>
  );
};

